import { getParentByLocation } from 'api/parent';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Stack,
  Typography,
} from '@mui/material';
import Overlay from 'components/Overlay';
import { AddressContext, AuthContext, SnackBarContext } from 'constants/contexts';
import useConfig from 'hooks/useConfig';
import React, {
  useContext, useEffect,
  useState,
} from 'react';
import { captureException, getHttpErrorMessage } from 'utils/error';
import { EventEmitter } from 'utils/event';
import { EVENTS } from 'constants/events';
import { env } from 'constants';
import { getGeoCode } from 'utils/places';
import AddressList from 'components/AddressList';
import IconButton from 'components/IconButton';
import { Box } from '@mui/system';
import SearchLocation from './SearchLocation';
import AddressForm from './AddressForm';
import { addAddress, editAddress } from '../requests';
import useAutoComplete from './useAutoComplete';

function AddressOverlay() {
  const [searchQuery, setSearchQuery] = useState('');

  const {
    refetch,
    editingAddress,
    setEditingAddress,
    modalOpen,
    closeModal,
    setAddress,
    addresses,
    currentAddress,
  } = useContext(AddressContext);
  const [selectedAddress, setSelectedAddress] = useState(currentAddress);
  const [submitting, setSubmitting] = useState(false);
  const { isLoggedIn } = useContext(AuthContext);
  const showSnackBar = useContext(SnackBarContext);
  const [loading, setLoading] = useState(false);
  const orgName = useConfig('general.name');
  const { options: searchAddresses, loading: searchLoading } = useAutoComplete(searchQuery);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    setSearchQuery('');
    setSelectedAddress(currentAddress);
  }, [modalOpen, currentAddress]);

  async function handleSubmit() {
    if (!isLoggedIn) {
      closeModal();
      setAddress(editingAddress);
      return;
    }
    setSubmitting(true);
    return (editingAddress.id ? editAddress : addAddress)(editingAddress)
      .then((adr) => {
        refetch();
        setAddress(adr);
        setEditingAddress(null);
      })
      .catch((e) => {
        captureException(e);
        showSnackBar({ message: getHttpErrorMessage(e), type: 'error' });
      }).finally(() => {
        setSubmitting(false);
      });
  }

  useEffect(() => {
    if (
      !editingAddress ||
      env.IS_MARKETPLACE ||
      env.IS_CATERING ||
      !editAddress.lat
    ) {
      return;
    }
    setLoading(true);
    getParentByLocation(editingAddress)
      .then((parents) => {
        setLoading(false);
        if (parents.length === 0) {
          showSnackBar({
            message: `
            ${orgName} 
            does not currently operate in this
            location. Follow along as we launch in new cities.
            `,
            type: 'error',
          });
          setEditingAddress(false);
        }
      })
      .catch((e) => {
        captureException(e);
        setLoading(false);
      });
  }, [editingAddress?.lat, editingAddress?.lng]);

  function renderActions() {
    if (editingAddress) {
      return (
        <LoadingButton disabled={loading || submitting} loading={submitting} size="large" variant="primary" onClick={handleSubmit}>
          Save and continue
        </LoadingButton>
      );
    }
    return (
      <Button
        disabled={loading || submitting || !selectedAddress}
        size="large"
        fullWidth
        variant="primary"
        onClick={() => {
          setAddress(selectedAddress);
          closeModal();
          EventEmitter.fire(EVENTS.SELECT_ADDRESS);
        }}
      >
        Continue
      </Button>
    );
  }

  function renderTitle() {
    if (editingAddress) {
      return (
        <Stack direction="horizontal" alignItems="center" gap={1}>
          <IconButton
            variant="link"
            name="arrow-withtale-left"
            onClick={() => { setEditingAddress(null); }}
          />
          <span>Edit Address</span>
        </Stack>
      );
    }
    return 'Address';
  }

  function renderContent() {
    if (!editingAddress) {
      return (
        <Stack>
          <Box sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#fff',
            p: 2,
            zIndex: 1,
          }}
          >
            <SearchLocation
              loading={searchLoading}
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </Box>

          {
            !searchQuery && addresses?.length > 0 && (
              <Typography
                variant="title-md"
                m={2}
              >
                Saved addresses
              </Typography>
            )
          }

          <AddressList
            selectedAddress={selectedAddress}
            onSelect={(adr) => {
              setSearchQuery('');
              if (!adr.lat) {
                setLoading(true);
                setEditingAddress(adr);
                getGeoCode(adr.placeId).then(({ lat, lng }) => {
                  setEditingAddress({ ...adr, lat, lng });
                }).finally(() => {
                  setLoading(false);
                });
              } else {
                setSelectedAddress(adr);
              }
            }}
            sx={{
              mx: {
                xs: 0,
                md: 2,
              },
            }}
            addresses={searchQuery ? searchAddresses : addresses}
            // addresses={[]}
            editable={!searchQuery}
            loading={searchLoading}
          />
        </Stack>
      );
    }
    return (
      <Stack p={2}>
        <AddressForm loading={loading} />
      </Stack>
    );
  }

  return (
    <Overlay
      open={modalOpen}
      onClose={closeModal}
      mobileFullScreen
      hashKey="address"
      minHeight={400}
    >
      <Overlay.Header showClose={!editingAddress}>
        {renderTitle()}
      </Overlay.Header>
      <Overlay.Content sx={{ p: 0 }}>
        {renderContent()}
      </Overlay.Content>
      {
        !searchQuery && (
          <Overlay.Actions>
            {renderActions()}
          </Overlay.Actions>
        )
      }

    </Overlay>
  );
}

export default AddressOverlay;
